import Tippy from "@tippyjs/react";
import classNames from "classnames";
import React, { useCallback, useEffect, useRef } from "react";
import { Container, Nav, Navbar } from "react-bootstrap"; // Added
import toast from "react-hot-toast";
import {
  FaCog,
  FaComment,
  FaPodcast,
  FaSearch,
  FaShoppingCart,
  FaSignOutAlt,
  FaUserCog,
} from "react-icons/fa";
import { Link, useLocation } from "react-router-dom"; // Added
import "tippy.js/dist/tippy.css";
import { useAppAuthState } from "../../hooks/use-app-auth-state";
import { useCart } from "../../hooks/use-cart";
import { SitePage } from "../../types/SitePage";
import CartBadge from "./CartBadge";
import "./TopNav.scss";

interface TopNavProps {
  page: SitePage;
}

const TopNav: React.FC<TopNavProps> = () => {
  const { fireUser, isUserAdmin } = useAppAuthState();
  const location = useLocation();
  const navbarRef = useRef<HTMLDivElement>(null);
  const { isCartEmpty } = useCart();

  // Collapse navbar when location changes
  useEffect(() => {
    if (navbarRef.current) {
      // Find the navbar collapse element and collapse it
      const collapseElement =
        navbarRef.current.querySelector(".navbar-collapse");
      if (collapseElement && collapseElement.classList.contains("show")) {
        // Trigger the collapse by clicking the toggle button
        const toggleButton = navbarRef.current.querySelector(".navbar-toggler");
        if (toggleButton) {
          (toggleButton as HTMLElement).click();
        }
      }
    }
  }, [location.pathname]);

  const handleCartClick = useCallback(() => {
    if (isCartEmpty) {
      toast.error("Your cart is empty. Add some contacts to get started.");
    } else {
      window.open("/cart", "_blank");
    }
  }, [isCartEmpty]);

  const showSettingsLink = !!fireUser && false;
  const settingsLink = (
    <Tippy content="Settings" trigger="mouseenter focus">
      <Nav.Link as={Link} to="/account/settings">
        <FaCog /> <span className="link-text">Settings</span>
      </Nav.Link>
    </Tippy>
  );

  return (
    <Navbar ref={navbarRef} expand="lg" className="top-nav sticky-top">
      <Container>
        <Navbar.Brand as={Link} to="/home">
          VCDB
        </Navbar.Brand>
        <Navbar.Toggle aria-controls="basic-navbar-nav" />
        <Navbar.Collapse id="basic-navbar-nav">
          <Nav className="ms-auto">
            <Tippy
              content={!!fireUser ? "Export Cart" : "Log In to Export Contacts"}
              trigger="mouseenter focus"
            >
              <Nav.Link
                onClick={handleCartClick}
                className={classNames("cart-link", {
                  "cart-link-full": !isCartEmpty,
                })}
              >
                <FaShoppingCart />
                {!isCartEmpty && <CartBadge />}
                <span className="link-text">Cart</span>
              </Nav.Link>
            </Tippy>
            <Tippy content="VC Database" trigger="mouseenter focus">
              <Nav.Link as={Link} to="/find-vc">
                <FaSearch /> <span className="link-text">VC Database</span>
              </Nav.Link>
            </Tippy>
            <Tippy content="Podcasts" trigger="mouseenter focus">
              <Nav.Link as={Link} to="/podcasts">
                <FaPodcast /> <span className="link-text">Podcasts</span>
              </Nav.Link>
            </Tippy>
            <Tippy content="Feedback" trigger="mouseenter focus">
              <Nav.Link as={Link} to="mailto:vcdb@georgemike.com">
                <FaComment /> <span className="link-text">Feedback</span>
              </Nav.Link>
            </Tippy>
            {!!fireUser ? (
              <>
                {isUserAdmin && (
                  <Tippy content="Admin" trigger="mouseenter focus">
                    <Nav.Link as={Link} to="/admin">
                      <FaUserCog /> <span className="link-text">Admin</span>
                    </Nav.Link>
                  </Tippy>
                )}
                {showSettingsLink && settingsLink}
                <Tippy content="Logout" trigger="mouseenter focus">
                  <Nav.Link as={Link} to="/logout">
                    <FaSignOutAlt /> <span className="link-text">Logout</span>
                  </Nav.Link>
                </Tippy>
                <Tippy content="Account" trigger="mouseenter focus">
                  <Nav.Link as={Link} to="/account">
                    {fireUser.email}
                  </Nav.Link>
                </Tippy>
              </>
            ) : (
              <>
                <Nav.Link as={Link} to="/login">
                  Login
                </Nav.Link>
                <Nav.Link as={Link} to="/register">
                  Register
                </Nav.Link>
              </>
            )}
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

export default TopNav;
